import React, { useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import moment from 'moment';

import {
	addDaysToRosterDate,
	compareRosterDates,
	enumerateRosterDates,
	getRosterWeekStart,
} from '../../../../common/utils/people/roster';
import Text from '../../../components/text/Text';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

const DATE_FORMAT = 'YYYY-MM-DD';
const WEEKDAYS = [
	{ key: 'mon', short: 'M', label: 'Monday' },
	{ key: 'tue', short: 'T', label: 'Tuesday' },
	{ key: 'wed', short: 'W', label: 'Wednesday' },
	{ key: 'thu', short: 'T', label: 'Thursday' },
	{ key: 'fri', short: 'F', label: 'Friday' },
	{ key: 'sat', short: 'S', label: 'Saturday' },
	{ key: 'sun', short: 'S', label: 'Sunday' },
];

export const getMonthStart = date => moment(date, DATE_FORMAT).startOf('month').format(DATE_FORMAT);

export const addMonths = (monthStart, months) => moment(monthStart, DATE_FORMAT)
	.add(months, 'month')
	.format(DATE_FORMAT);

// Monday-first weeks covering the whole month (what the grid shows and what to load).
export const getMonthGridRange = (monthStart) => {
	const monthEnd = moment(monthStart, DATE_FORMAT).endOf('month').format(DATE_FORMAT);

	return {
		fromDate: getRosterWeekStart(monthStart),
		toDate: addDaysToRosterDate(getRosterWeekStart(monthEnd), 6),
	};
};

/**
 * MonthCalendar: month grid with Monday-first weeks (My roster, date fields).
 * Props:
 * - month: first day of the shown month (YYYY-MM-DD); onChangeMonth(nextMonthStart)
 * - selectedDate, onSelectDate(date); today (outlined)
 * - markedDates: { [date]: { shift, openShift } } dots under the day number
 * - minimumDate: earlier days cannot be picked
 * - showLegend: "Your shift" / "Open shift" key under the grid
 */
const MonthCalendar = ({
	month,
	onChangeMonth,
	selectedDate,
	onSelectDate,
	today,
	markedDates,
	minimumDate,
	showLegend = false,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();

	const weeks = useMemo(() => {
		const { fromDate, toDate } = getMonthGridRange(month);
		const dates = enumerateRosterDates(fromDate, toDate);
		const rows = [];
		for (let index = 0; index < dates.length; index += 7) {
			rows.push(dates.slice(index, index + 7));
		}
		return rows;
	}, [month]);
	const monthKey = month.slice(0, 7);
	const monthLabel = moment(month, DATE_FORMAT).format('MMMM YYYY');

	const renderDay = (date) => {
		const dayNumber = Number(date.slice(8, 10));
		if (date.slice(0, 7) !== monthKey) {
			return (
				<View key={date} style={styles.day} importantForAccessibility="no">
					<Text style={[styles.dayNumber, styles.dayNumberOutside]}>{dayNumber}</Text>
					<View style={styles.markers} />
				</View>
			);
		}

		const marks = markedDates?.[date] || {};
		const isSelected = date === selectedDate;
		const isToday = date === today;
		const isDisabled = Boolean(minimumDate) && compareRosterDates(date, minimumDate) < 0;
		const accessibilityLabel = [
			moment(date, DATE_FORMAT).format('dddd D MMMM'),
			isToday ? 'today' : null,
			marks.shift ? 'your shift' : null,
			marks.openShift ? 'open shift' : null,
		].filter(Boolean).join(', ');

		return (
			<Pressable
				key={date}
				onPress={() => onSelectDate(date)}
				disabled={isDisabled}
				accessibilityRole="button"
				accessibilityLabel={accessibilityLabel}
				accessibilityState={{ selected: isSelected, disabled: isDisabled }}
				style={({ pressed }) => [
					styles.day,
					isToday && styles.dayToday,
					isSelected && styles.daySelected,
					pressed && styles.pressed,
				]}
			>
				<Text
					style={[
						styles.dayNumber,
						(isToday || isSelected) && styles.dayNumberStrong,
						isSelected && styles.dayNumberSelected,
						isDisabled && styles.dayNumberDisabled,
					]}
				>
					{dayNumber}
				</Text>
				<View style={styles.markers}>
					{Boolean(marks.shift) && <View style={[styles.shiftDot, isSelected && styles.shiftDotSelected]} />}
					{Boolean(marks.openShift) && <View style={[styles.openShiftDot, isSelected && styles.openShiftDotSelected]} />}
				</View>
			</Pressable>
		);
	};

	return (
		<View style={styles.calendar}>
			<View style={styles.header}>
				<Pressable
					onPress={() => onChangeMonth(addMonths(month, -1))}
					accessibilityRole="button"
					accessibilityLabel="Previous month"
					style={({ pressed }) => [styles.monthButton, pressed && styles.pressed]}
				>
					<Feather name="chevron-left" size={20} color={colors.accent} />
				</Pressable>
				<Text style={styles.monthLabel} accessibilityRole="header">{monthLabel}</Text>
				<Pressable
					onPress={() => onChangeMonth(addMonths(month, 1))}
					accessibilityRole="button"
					accessibilityLabel="Next month"
					style={({ pressed }) => [styles.monthButton, pressed && styles.pressed]}
				>
					<Feather name="chevron-right" size={20} color={colors.accent} />
				</Pressable>
			</View>
			<View style={styles.week}>
				{WEEKDAYS.map(weekday => (
					<Text key={weekday.key} style={styles.weekday} accessibilityLabel={weekday.label}>
						{weekday.short}
					</Text>
				))}
			</View>
			{weeks.map(week => (
				<View key={week[0]} style={styles.week}>
					{week.map(renderDay)}
				</View>
			))}
			{showLegend && (
				<View style={styles.legend}>
					<View style={styles.legendItem}>
						<View style={styles.shiftDot} />
						<Text style={styles.legendLabel}>Your shift</Text>
					</View>
					<View style={styles.legendItem}>
						<View style={styles.openShiftDot} />
						<Text style={styles.legendLabel}>Open shift</Text>
					</View>
				</View>
			)}
		</View>
	);
};

const DOT_SIZE = 6;

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	calendar: {
		gap: space.xs,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: space.sm,
	},
	monthButton: {
		width: 44,
		height: 44,
		borderRadius: radius.pill,
		alignItems: 'center',
		justifyContent: 'center',
	},
	monthLabel: {
		...type.sectionTitle,
		color: colors.text,
	},
	week: {
		flexDirection: 'row',
		gap: space.xs,
	},
	weekday: {
		...type.label,
		flex: 1,
		textAlign: 'center',
		color: colors.textSecondary,
		paddingBottom: space.xs,
	},
	day: {
		flex: 1,
		height: 48,
		borderRadius: radius.card,
		borderWidth: 2,
		borderColor: 'transparent',
		alignItems: 'center',
		justifyContent: 'center',
		gap: space.xs,
	},
	dayToday: {
		borderColor: colors.accent,
	},
	daySelected: {
		borderColor: colors.primary,
		backgroundColor: colors.primary,
	},
	pressed: {
		opacity: 0.8,
	},
	dayNumber: {
		...type.bodySmallStrong,
		color: colors.text,
	},
	dayNumberStrong: {
		...type.bodySmallSemibold,
	},
	dayNumberSelected: {
		color: colors.onPrimary,
	},
	dayNumberOutside: {
		...type.bodySmall,
		color: colors.textFaint,
	},
	dayNumberDisabled: {
		color: colors.disabledText,
	},
	markers: {
		height: DOT_SIZE,
		flexDirection: 'row',
		gap: 3,
	},
	shiftDot: {
		width: DOT_SIZE,
		height: DOT_SIZE,
		borderRadius: DOT_SIZE / 2,
		backgroundColor: colors.accent,
	},
	shiftDotSelected: {
		backgroundColor: colors.onPrimary,
	},
	openShiftDot: {
		width: DOT_SIZE,
		height: DOT_SIZE,
		borderRadius: DOT_SIZE / 2,
		borderWidth: 1.5,
		borderColor: colors.warning,
	},
	openShiftDotSelected: {
		borderColor: colors.onPrimary,
	},
	legend: {
		flexDirection: 'row',
		justifyContent: 'center',
		gap: space.gutter,
		marginTop: space.sm,
	},
	legendItem: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 6,
	},
	legendLabel: {
		...type.caption,
		color: colors.textSecondary,
	},
});

export default MonthCalendar;
